import { Component, Injector, OnInit, Inject, AfterViewInit, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { createCustomElement } from '@angular/elements';
import { MsalService } from '@azure/msal-angular';
import { CONFIG_ENV } from '@core/config/config.service';
import { IEnvironmentVariables } from '@env/IEnvironment';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { SharedServiceComponent } from '@shared/components/shared-service/shared-service.component';
import { AuthenticationService } from '@core/authentication/services';
import { map } from 'rxjs/operators';
import { AccessibilityObserverService } from '@core/services/accessibility-observer.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  providers: [
    AccessibilityObserverService,
  ]
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'user-management';
  initComplete = false;

  constructor(
    private injector: Injector,
    private router: Router,
    private ngZone: NgZone,
    private accessibilityObserverService: AccessibilityObserverService,
    private msalService: MsalService,
    private readonly authenticationService: AuthenticationService,
    private securityService: SecurityService,
    @Inject(CONFIG_ENV) private config: IEnvironmentVariables
  ) {
    // Register the custom element only once to avoid `NotSupportedError`
    // This error occurs if `customElements.define` is called more than once
    if (!customElements.get('mnc-shared-services')) {
      customElements.define('mnc-shared-services', createCustomElement(SharedServiceComponent, { injector: this.injector }));
    }
  }

  ngOnInit(): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: (authResult) => {
        if (authResult && (authResult?.idTokenClaims as any)['tfp'] === this.config.b2cPolicies.names.forgotPassword) {
          this.msalService.logout();
        }
        else if (this.authenticationService.user) {
          this.initializePermission();
        }
      },
      error: (error) => {
        this.handleLoginError(error);
      },
    });
  }

  ngAfterViewInit(): void {
    this.ngZone.onStable.pipe(map(() => true))
      .subscribe(() => {
        this.accessibilityObserverService.start();
      });
  }

  private handleLoginError(error: { errorMessage: string[] }): void {
    if (!error) {
      return;
    }

    const USER_FORGOT_PASSWORD_CODE = 'AADB2C90118';
    const USER_CANCEL_FLOW_CODE = 'AADB2C90091';

    if (error.errorMessage.includes(USER_FORGOT_PASSWORD_CODE)) {
      this.msalService.loginRedirect({
        authority: `${this.config.b2cPolicies.authorityBaseUrl}/${this.config.b2cPolicies.names.forgotPassword}`,
        scopes: this.config.authConfig.angularConfig.consentScopes,
      });
    }

    if (error.errorMessage.includes(USER_CANCEL_FLOW_CODE)) {
      this.msalService.loginRedirect({
        authority: `${this.config.b2cPolicies.authorityBaseUrl}/${this.config.b2cPolicies.names.signUpSignIn}`,
        scopes: this.config.authConfig.angularConfig.consentScopes,
      });
    }
  }
  private initializePermission(): void {
    this.authenticationService.getPermissions().subscribe({
      next: permissions => {
        this.securityService.setUserPermissions(
          Object.values(permissions).flat()
        );
        this.securityService.setAllowEmptyPermission(false);
        this.initComplete = true;
      },
    });
  }
}
